import { BREAKPOINT_DESKTOP } from "../../../constants/constants";
import truncateTime, {
  ONE_HOUR_OF_MS,
  ONE_MINUTE_OF_MS,
} from "../../../utils/timeUtils";

import { TargetSoFarStyles } from "./HubletViewStyles/HubletViewStyles";
import { isEqual } from "lodash";
import { Tooltip, useMediaQuery } from "@mui/material";
import { calcRollup } from "../../../utils/metrics";
import { DimensionObject } from "../../../utils/useContainerDimensions";
import {
  CUSTOMERS_PROCESSES_WITH_CUSTOM_PNL_CATEGORY_NAMES,
  CUSTOMERS_WITH_CUSTOM_DETAILS_VIEW_BOTTOM_ITEMS,
} from "../../../constants/customers";
import type { DataDumpRow, TimeRangeFinite } from "../../../types/types";
import { useMapDataDumpToRollup } from "../TrendChart/TrendChartContent/trendChartContentUtils";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import {
  useSelectedShift,
  useCurrentShift,
  useHubletViewDataDump,
  useVisibleGroups,
  useSummaryGroup,
  useDashboardPageContext,
  useHubletViewRollingWindowDataDump,
  useFocusRange,
  useSegmentsForProcess,
  useRollupDisplayForProcess,
  useIsMinWidth,
  useGetPageFromPath,
  useHideOverflow,
  useDetailKey,
  useMidgardIdHubletPage,
} from "utils/customHooks/genericHooks";
import { useDataDumpRows } from "./SingleTimeline/singleTimelineStore";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import i18next from "i18next";
import { useLastDataDumpRow } from "components/DataGetter/LastDataDumpRowGetter";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { getCallAndLastSegmentDetails } from "../Tiles/ProcessTile/processTileUtils";
import { useShouldUseDataDumpForHubletView } from "utils/customHooks/whichServerHooks";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useStatusTime } from "../Tiles/ProcessTile/ProcessTile";
import {
  useMetrics,
  useIsMidgardDDAEnabled,
} from "components/Dashboard/GroupMetrics/useMetrics";
import { useSelector } from "store/useSelector";
import { useMemo } from "react";
import { atom, useAtom } from "jotai";
import { PnlCategoriesEnum } from "types/enums";
import { useCurrentAskrSegment } from "components/DataGetter/CurrentSegmentGetter";
import { useProcessPlaybook } from "components/DataGetter/CurrentPlaybookGetter";

export const isTruthyOrZero = n => Boolean(n || n === 0);

const getTimeWindowDisplay = timeWindow => {
  switch (timeWindow) {
    case ONE_HOUR_OF_MS:
      return "hr";
    case ONE_MINUTE_OF_MS:
      return "min";
    case 1000:
      return "sec";
    default:
      return "min";
  }
};

const EMPTY_ITEM = { top: "", middle: "", bottom: "" };

function useHubletPageProcessId() {
  const isStorybook = useIsStorybook();
  const visibleGroups = useVisibleGroups();
  const detailKey = useDetailKey();
  const summaryGroup = useSummaryGroup();
  const currentGroup = visibleGroups.find(
    group => group.summaryGroup === summaryGroup
  );
  const process = currentGroup?.processes?.[isStorybook ? 0 : detailKey];
  const processId = process?.id;
  return processId;
}
const FEB_13_2020_1PM = 1581620000000;

export function useBottomLeftItem() {
  const [flightConfig] = useFlightConfig();
  const processId = useHubletPageProcessId();

  const {
    showCountsSinceShiftStartOnly,
    hideCountsLeftSide,
    hideTargetSoFar,
    targetOutputIgnorePlanned,
  } = flightConfig.hubletView;
  const dataDumpAggregate = useHubletViewDataDump();
  const ckey = useCustomerKey();

  const hubletViewBottomRowItemsConfig = useHubletViewBottomRowItemsConfig();

  const customItem1FromMidgardFlightConfig =
    hubletViewBottomRowItemsConfig?.item1 &&
    "displayName" in hubletViewBottomRowItemsConfig.item1
      ? hubletViewBottomRowItemsConfig?.item1
      : null;

  const customItem1 = processId
    ? (customItem1FromMidgardFlightConfig ??
      hubletViewBottomRowItemsConfig?.item1?.[processId] ??
      hubletViewBottomRowItemsConfig?.item1?.ALL_PROCESSES)
    : null;

  const customItem1Value =
    customItem1?.dataDumpName &&
    dataDumpAggregate?.[String(customItem1?.dataDumpName)];

  const shouldShowCustomItem1 = Boolean(customItem1?.dataDumpName);
  const isStorybook = useIsStorybook();
  const dataDumpAggregateRollingWindow = useHubletViewRollingWindowDataDump();
  const outputCount = useHubletViewOutputCount();

  const item1Value = shouldShowCustomItem1 ? customItem1Value : outputCount;
  const { isFullDaySelected } = useDashboardPageContext();
  const [lastDataDumpRow] = useLastDataDumpRow();
  const now = isStorybook ? FEB_13_2020_1PM : Date.now();
  const skuStartMs = lastDataDumpRow?.skuStart;
  const focusRange = useFocusRange();
  const shiftStart = focusRange?.ge;

  const shiftOrSkuStartTime = skuStartMs
    ? Math.max(shiftStart || 0, skuStartMs)
    : 0;

  const shiftOrSkuOrRollingWindowStartTime = dataDumpAggregateRollingWindow
    ? dataDumpAggregateRollingWindow.range.ge
    : skuStartMs
      ? shiftOrSkuStartTime
      : 0;

  const processSegments = useSegmentsForProcess(processId);

  const rollupSinceShiftOrSkuStart =
    processSegments && shiftStart
      ? getRollup(processSegments, Math.max(skuStartMs || 0, shiftStart))
      : null;

  const NPSTime = dataDumpAggregateRollingWindow
    ? dataDumpAggregateRollingWindow?.notScheduledTime
    : rollupSinceShiftOrSkuStart?.notScheduled;

  const skuTimeSoFarWithoutNPS = isStorybook
    ? 1000
    : skuStartMs
      ? now - shiftOrSkuOrRollingWindowStartTime - (NPSTime || 0)
      : 0; // don't count NPS time in calculating the target

  const skuTimeSoFarMS = Math.max(
    // minimum 1 second to avoid flash of huge numbers (target count = target rate / sku time so far without NPS time)
    1000,
    skuTimeSoFarWithoutNPS || 0
  );

  const targetRatePerMS = getTargetRatePerMs(
    lastDataDumpRow?.targetRate,
    lastDataDumpRow?.rateDenominator
  );
  // if we're in a previous shift, use the shift duration
  const isPrevShift = focusRange && focusRange.lt < Date.now();
  const [dataDumpRows] = useDataDumpRows();
  const { prevShiftTargetRatePerMs, prevShiftTarget } = getPrevShiftTarget(
    focusRange,
    dataDumpRows[processId],
    targetOutputIgnorePlanned
  );

  const productionTimeSoFarThisShift =
    focusRange &&
    now - focusRange.ge - (dataDumpAggregate?.notScheduledTime || 0);
  const shiftTimeSoFarMs = !dataDumpAggregate
    ? null
    : productionTimeSoFarThisShift;

  const targetRate = isPrevShift ? prevShiftTargetRatePerMs : targetRatePerMS;

  const targetSoFar = isPrevShift
    ? prevShiftTarget
    : showCountsSinceShiftStartOnly && shiftTimeSoFarMs && targetRate
      ? targetRate * shiftTimeSoFarMs // rate for the entire shift
      : !targetRate || !skuTimeSoFarMS || skuTimeSoFarMS < 1000 // prevent flickering when we get tiny skuTimeSoFarMS
        ? 0
        : (targetRate || targetRate === 0) && targetRate * skuTimeSoFarMS; // rate for the current sku

  // are we looking since the start of the sku or the start of the shift?
  const doesHaveSkuFromMidgard = Boolean(
    skuStartMs && skuStartMs !== shiftStart
  );
  const customItem1Units = String(customItem1?.displayName ?? "");

  const bottomRowItemsConfig =
    flightConfig?.hubletView.bottomRowItemsByProcess?.[processId] ??
    flightConfig?.hubletView.bottomRowItems;

  const outputLabel = String(
    bottomRowItemsConfig?.item1?.displayName ?? "parts"
  );
  const { translate } = useFlightTranslation();
  // item1 (leftmost) is count this shift this sku (because this shift, we can't calculate the scaled output)
  const item1 = {
    id: 1,
    top:
      targetSoFar && !hideTargetSoFar ? (
        <TargetSoFarStyles>
          <div>{translate("target_count") + ":"}</div>
          <div
            className="targetSoFarAmount"
            data-server-names={[SERVER_NAMES.midgardLastDataDumpRow]}
          >
            <span className="callout">
              {formatWithCommas(Math.round(targetSoFar))}
            </span>
          </div>
        </TargetSoFarStyles>
      ) : (
        ""
      ),
    // if item1Value is 0, show it
    middle: (
      <span className={item1Value >= 10000 ? "long-number" : ""}>
        {item1Value === 0 || item1Value
          ? formatWithCommas(Math.round(item1Value))
          : ""}
      </span>
    ),
    bottom:
      item1Value === 0 || item1Value ? (
        <div className="bottomRowItem3">
          <div
            className="top"
            data-server-names={[SERVER_NAMES.midgardDataDumpHubletView]}
          >
            {translate(
              customItem1Units ? String(customItem1Units) : outputLabel
            )}
          </div>
          <div className="bottom">
            ({isFullDaySelected ? "today" : translate("this_shift")}
            {doesHaveSkuFromMidgard && !showCountsSinceShiftStartOnly
              ? ", " + (ckey === "ivc" ? "this FG" : translate("this_sku"))
              : ""}
            )
          </div>
        </div>
      ) : (
        ""
      ),
  };
  const shouldHideLeftSide = hideCountsLeftSide;
  return shouldHideLeftSide ? { ...EMPTY_ITEM, id: 1 } : item1;
}
function useHubletViewBottomRowItemsConfig() {
  const [flightConfig] = useFlightConfig();
  const processId = useHubletPageProcessId();

  const ckey = useCustomerKey();

  return (
    (processId
      ? flightConfig?.hubletView.bottomRowItemsByProcess?.[processId]
      : null) ??
    flightConfig?.hubletView.bottomRowItems ??
    CUSTOMERS_WITH_CUSTOM_DETAILS_VIEW_BOTTOM_ITEMS[ckey]
  );
}

function useHubletViewOutputCount() {
  const processId = useHubletPageProcessId();
  const hubletViewBottomRowItemsConfig = useHubletViewBottomRowItemsConfig();

  const customItem1FromMidgardFlightConfig =
    hubletViewBottomRowItemsConfig?.item1 &&
    "displayName" in hubletViewBottomRowItemsConfig.item1
      ? hubletViewBottomRowItemsConfig?.item1
      : null;
  const customItem1 = processId
    ? (customItem1FromMidgardFlightConfig ??
      hubletViewBottomRowItemsConfig?.item1?.[processId] ??
      hubletViewBottomRowItemsConfig?.item1?.ALL_PROCESSES)
    : null;
  const countsDataDumpName = String(customItem1?.dataDumpName ?? "netOutput");
  const dataDumpAggregate = useHubletViewDataDump();

  return dataDumpAggregate?.[countsDataDumpName] ?? null;
}

export function useBottomMiddleItem(): {
  id: number;
  top: string | React.JSX.Element;
  middle: React.JSX.Element;
  bottom: React.JSX.Element | string | undefined;
} {
  const dataDump = useHubletViewDataDump();
  const hubletViewBottomRowItemsConfig = useHubletViewBottomRowItemsConfig();
  const shouldUseDataDumps = useShouldUseDataDumpForHubletView();
  const processId = useHubletPageProcessId();
  const rollupMetricFromAskr = useRollupDisplayForProcess(processId);
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const mapDataDumpToRollup = useMapDataDumpToRollup();
  const rollupMetric =
    shouldUseDataDumps && dataDump
      ? mapDataDumpToRollup(dataDump)
      : rollupMetricFromAskr;

  const customDataDumpMetricToDisplay = processId
    ? hubletViewBottomRowItemsConfig?.item2?.[processId] ||
      hubletViewBottomRowItemsConfig?.item2?.ALL_PROCESSES
    : null;
  const customMetricDisplay = customDataDumpMetricToDisplay?.dataDumpName
    ? dataDump?.[customDataDumpMetricToDisplay.dataDumpName]?.toFixed(0)
    : null;
  const { preferredMetric } = useSettings();
  const { metrics } = useMetrics();
  const processSegments = useSegmentsForProcess(processId);

  const preferredMetricData = metrics.find(
    m => m.dataDumpKey === preferredMetric
  );
  const { metricDisplay } = getCallAndLastSegmentDetails({
    dataDumpsObjForFullRange: dataDump,
    rollupMetric,
    preferredMetric,
    // in bottom-middle, we show the value of last segment within the focus range
    lastSegment: processSegments
      ? processSegments[processSegments.length - 1]
      : null,
    isMidgardDDAEnabled,
  });

  const metricValue = customDataDumpMetricToDisplay
    ? customMetricDisplay
    : metricDisplay;

  const item2Middle =
    (typeof metricValue === "number" && metricValue !== 0
      ? metricValue.toFixed(0)
      : metricValue) || "--";
  const customMetricDisplayName =
    customDataDumpMetricToDisplay?.displayName || null;

  const { isFullDaySelected } = useDashboardPageContext();
  const [
    {
      hubletView: { showCountsSinceShiftStartOnly },
    },
  ] = useFlightConfig();
  const focusRange = useFocusRange();
  const shiftStart = focusRange?.ge;
  const [lastDataDumpRow] = useLastDataDumpRow();
  const skuStartMs = lastDataDumpRow?.skuStart;
  // are we looking since the start of the sku or the start of the shift?
  const doesHaveSkuFromMidgard = Boolean(
    skuStartMs && skuStartMs !== shiftStart
  );
  const { translate } = useFlightTranslation();
  const ckey = useCustomerKey();
  return useMemo(() => {
    const top = customMetricDisplayName
      ? customMetricDisplayName === "EMPTY"
        ? ""
        : customMetricDisplayName
      : (preferredMetricData?.displayName ?? "");

    const item2 = {
      id: 2,
      top,
      middle: (
        <div
          data-server-names={[
            shouldUseDataDumps
              ? SERVER_NAMES.midgardDataDumpHubletView
              : SERVER_NAMES.askrAggregates,
          ]}
        >
          {item2Middle}
        </div>
      ),
      bottom: (
        <div className="bottomRowItem3">
          <div
            className="top"
            data-server-names={[SERVER_NAMES.midgardDataDumpHubletView]}
          >
            {preferredMetricData?.units === "EMPTY"
              ? ""
              : metricValue && preferredMetricData?.units
                ? preferredMetricData?.units
                : ""}
          </div>
          <div className="bottom">
            ({isFullDaySelected ? "today" : translate("this_shift")}
            {doesHaveSkuFromMidgard && !showCountsSinceShiftStartOnly
              ? ", " + (ckey === "ivc" ? "this FG" : translate("this_sku"))
              : ""}
            )
          </div>
        </div>
      ),
    };
    return item2;
  }, [
    customMetricDisplayName,
    item2Middle,
    metricValue,
    preferredMetricData?.displayName,
    preferredMetricData?.units,
    shouldUseDataDumps,
    ckey,
    doesHaveSkuFromMidgard,
    isFullDaySelected,
    showCountsSinceShiftStartOnly,
    translate,
  ]);
}

export function useBottomRightItem(): {
  id: number;
  top: string | React.JSX.Element;
  middle: any;
  bottom: string | React.JSX.Element;
} {
  const { translate } = useFlightTranslation();
  const processId = useHubletPageProcessId();
  const [
    {
      hubletView: {
        showCountsSinceShiftStartOnly,
        hideTargetsRightSide,
        hideTargetRate,
        targetOutputIgnorePlanned,
      },
    },
  ] = useFlightConfig();
  const dataDumpAggregateThisShiftThisSku = useHubletViewDataDump();
  const ckey = useCustomerKey();
  const hubletViewBottomRowItemsConfig = useHubletViewBottomRowItemsConfig();

  const isStorybook = useIsStorybook();

  const { isFullDaySelected } = useDashboardPageContext();
  const [lastDataDumpRow] = useLastDataDumpRow();
  const skuStartMs = lastDataDumpRow?.skuStart;
  const focusRange = useFocusRange();
  const shiftStart = focusRange?.ge;

  const targetRatePerMS = getTargetRatePerMs(
    lastDataDumpRow?.targetRate,
    lastDataDumpRow?.rateDenominator
  );
  // if we're in a previous shift, use the shift duration
  const isPrevShift = focusRange && focusRange.lt < Date.now();

  const [dataDumpRows] = useDataDumpRows();
  const { prevShiftTargetRatePerMs } = getPrevShiftTarget(
    focusRange,
    dataDumpRows[processId],
    targetOutputIgnorePlanned
  );

  const customTargetRate = hubletViewBottomRowItemsConfig?.targetRate;

  const customTargetRateUnits = customTargetRate?.units;
  const customTargetRateUnitsNumerator = customTargetRateUnits
    ? ` ${customTargetRateUnits}`
    : "";
  const customTargetRateDecimalPoints = customTargetRate?.decimalPoints;

  const targetRate = isPrevShift ? prevShiftTargetRatePerMs : targetRatePerMS;
  const teamSize = lastDataDumpRow?.teamSize;

  const [processPlaybook] = useProcessPlaybook();
  const currentSkuPlaybook = processPlaybook?.find(
    skuPlaybook => skuPlaybook.skuCode === lastDataDumpRow?.skuCode
  );
  const currentPlay = currentSkuPlaybook?.plays?.find(
    play => play.teamSize === teamSize
  );
  const doAllPlaysHaveSameTargetRate =
    currentSkuPlaybook?.plays?.every(
      play =>
        play.targetProducingRate.count ===
        currentPlay?.targetProducingRate.count
    ) ?? false;

  const timeWindowDisplay = lastDataDumpRow?.rateDenominator
    ? getTimeWindowDisplay(lastDataDumpRow?.rateDenominator)
    : "";

  const rateDenominator = lastDataDumpRow?.rateDenominator;
  const targetRateDisplay =
    (targetRate || targetRate === 0) && rateDenominator
      ? (targetRate * rateDenominator).toFixed(
          (customTargetRateDecimalPoints ?? Math.abs(targetRate) < 10) ? 1 : 0
        )
      : "";

  const originalTargetRate =
    teamSize &&
    rateDenominator &&
    (targetRate || targetRate === 0) &&
    ((targetRate * rateDenominator) / teamSize).toFixed(
      Math.abs((targetRate * rateDenominator) / teamSize) < 10 ? 1 : 0
    );
  const shouldShowTeamSizeMultiplierIndicator =
    teamSize &&
    teamSize > 1 &&
    targetRateDisplay &&
    !doAllPlaysHaveSameTargetRate;
  const customItem3FromMidgardFlightConfig =
    hubletViewBottomRowItemsConfig?.item3 &&
    "displayName" in hubletViewBottomRowItemsConfig.item3
      ? hubletViewBottomRowItemsConfig?.item3
      : null;
  const customItem3 = processId
    ? (customItem3FromMidgardFlightConfig ??
      hubletViewBottomRowItemsConfig?.item3?.[processId] ??
      hubletViewBottomRowItemsConfig?.item3?.ALL_PROCESSES)
    : null;
  const shouldShowCustomItem3 = Boolean(customItem3?.dataDumpName);
  const customItem3Value =
    customItem3?.dataDumpName &&
    dataDumpAggregateThisShiftThisSku?.[String(customItem3?.dataDumpName)];
  const customItem3Units = customItem3?.displayName;

  const { rateRollingTimeWindow, isUsingRateRollingTimeWindow } = useSettings();
  const hubletViewCurrentRatePerMs = useSelector(
    state => state.legacy.hubletViewCurrentRatePerMs
  );
  const currentRateBeforeCustomCheck =
    hubletViewCurrentRatePerMs &&
    rateDenominator &&
    hubletViewCurrentRatePerMs * rateDenominator;

  const currentRate = shouldShowCustomItem3
    ? customItem3Value
    : currentRateBeforeCustomCheck;

  // are we looking since the start of the sku or the start of the shift?
  const doesHaveSkuFromMidgard = Boolean(
    skuStartMs && skuStartMs !== shiftStart
  );
  const outputLabel = hubletViewBottomRowItemsConfig?.item3?.displayName;
  const outputLabelDisplay = outputLabel || translate("parts");

  /** show "parts/min" only if rate is 0 and no sku is selected (i.e. no rateDenominator) */
  const shouldShowDefaultMinLabel = !timeWindowDisplay && currentRate === 0;

  const item3 = {
    id: 3,
    top:
      !targetRate || hideTargetRate ? (
        ""
      ) : (
        <div data-server-names={[SERVER_NAMES.midgardLastDataDumpRow]}>
          {translate("target_rate") + ": "}
          <span className="targetRateContent">
            {shouldShowTeamSizeMultiplierIndicator ? (
              <span className="teamSizeMultiplierIndicator">
                ({teamSize} x {originalTargetRate})
              </span>
            ) : null}
            <span className="callout">
              {formatWithCommas(Number(targetRateDisplay))}
            </span>
          </span>
          {`${customTargetRateUnitsNumerator}/${timeWindowDisplay}`}
        </div>
      ),
    middle:
      (typeof currentRate === "number" ? (
        <span className={currentRate >= 10000 ? "long-number" : ""}>
          {formatWithCommas(
            Number(
              currentRate.toFixed(currentRate > 1 ? 0 : 1) // show one decimal if it's <1
            )
          )}
        </span>
      ) : (
        currentRate
      )) || "",
    bottom:
      typeof currentRate === "number" ? (
        <div
          className="bottomRowItem3"
          data-server-names={[
            SERVER_NAMES.midgardLastDataDumpRow,
            SERVER_NAMES.midgardDataDumpHubletView,
          ]}
        >
          <div className="top">
            {ckey === "kruger"
              ? "stops/hr"
              : translate(
                  `${customItem3Units || outputLabelDisplay}${
                    timeWindowDisplay
                      ? "/" + timeWindowDisplay
                      : shouldShowDefaultMinLabel
                        ? "/min"
                        : ""
                  }`
                )}
          </div>
          {isUsingRateRollingTimeWindow ? (
            isStorybook /* storybook can't handle Tooltip? */ ? (
              <RateRollingTimeWindowIndicator
                numMinutesString={(
                  rateRollingTimeWindow / ONE_MINUTE_OF_MS
                ).toFixed(0)}
              />
            ) : (
              <Tooltip title="change this value on the Settings page">
                <div>
                  <RateRollingTimeWindowIndicator
                    numMinutesString={(
                      rateRollingTimeWindow / ONE_MINUTE_OF_MS
                    ).toFixed(0)}
                  />
                </div>
              </Tooltip>
            )
          ) : (
            <div className="bottom">
              ({isFullDaySelected ? "today" : translate("this_shift")}
              {doesHaveSkuFromMidgard && !showCountsSinceShiftStartOnly
                ? ckey === "ivc"
                  ? ", this FG"
                  : ", " + translate("this_sku")
                : ""}
              )
            </div>
          )}
        </div>
      ) : (
        ""
      ),
  };
  const shouldHideRightSide = hideTargetsRightSide;

  return shouldHideRightSide ? { ...EMPTY_ITEM, id: 3 } : item3;
}
function formatWithCommas(num: number) {
  const formatted =
    num >= 10000 ? new Intl.NumberFormat(i18next.language).format(num) : num;

  return formatted;
}

export const getRollup = (segments, shiftOrSkuStart, ltTime = Date.now()) => {
  if (!segments) {
    return null;
  }
  return calcRollup(segments, {
    ge: shiftOrSkuStart,
    lt: ltTime,
  });
};

function getPrevShiftTarget(
  focusRange: TimeRangeFinite | null,
  dataDumpRows: DataDumpRow[] | null,
  ignorePlanned: boolean
) {
  if (!dataDumpRows || dataDumpRows.length === 0) {
    return { prevShiftTargetRatePerMs: null, prevShiftTarget: null };
  }
  let prevShiftDurationScheduled = 0;
  const categoriesToExclude = [
    "notScheduled",
    ...(ignorePlanned ? ["planned"] : []),
  ];
  const prevShiftTarget = dataDumpRows.reduce(
    (acc, cur) => {
      if (categoriesToExclude.includes(cur.pnlCategory)) {
        return acc;
      }
      // clamp the segment at the shift
      const geClampedAtShift = Math.max(Number(cur.ge), focusRange?.ge ?? 0);
      const ltClampedAtShift = Math.min(Number(cur.lt), focusRange?.lt ?? 0);
      const duration = Math.max(0, ltClampedAtShift - geClampedAtShift);

      prevShiftDurationScheduled += duration;

      const segmentTarget =
        // * midgard targetRate already includes skuScale & teamSize
        duration * (cur.targetRate / cur.rateDenominator);

      return segmentTarget ? Number(acc) + segmentTarget : acc;
    },
    null as number | null
  );

  const prevShiftTargetRatePerMs =
    prevShiftTarget && prevShiftDurationScheduled
      ? prevShiftTarget / prevShiftDurationScheduled
      : null;
  return { prevShiftTargetRatePerMs, prevShiftTarget };
}

export function getTargetRatePerMs(
  targetRate?: number, // * midgard targetRate already includes skuScale
  rateDenominator?: number // * midgard targetRate already includes skuScale
) {
  const targetRatePerMS =
    (targetRate || targetRate === 0) &&
    rateDenominator &&
    targetRate / rateDenominator;
  return targetRatePerMS;
}

function RateRollingTimeWindowIndicator({
  numMinutesString,
}: {
  numMinutesString: string;
}) {
  return (
    <div className="bottom">(over the last {numMinutesString} minutes)</div>
  );
}

export function getMinimumOfWidthOrHeight(dimensions: DimensionObject) {
  return dimensions.width && dimensions.height
    ? Math.min(dimensions.width, dimensions.height)
    : 0;
}

/** returns max(shiftStart, skuStart) for the HubletView process & sku,
 * to use as ge in the data dump query
 */
export function useGeForDataDumpQueryHubletView(): number {
  const currentShift = useCurrentShift();
  const selectedShift = useSelectedShift();
  const isCurrentShiftSelected = isEqual(currentShift, selectedShift);
  const [
    {
      hubletView: { showCountsSinceShiftStartOnly },
    },
  ] = useFlightConfig();

  const shiftStart = selectedShift.range.ge;

  // use the latest data dump row to get the skuStart (cheaper than getting the whole DDA first)
  const [lastDataDumpRow] = useLastDataDumpRow();

  const skuStartMs = lastDataDumpRow?.skuStart;
  const skuName = lastDataDumpRow?.skuName;
  const skuCode = lastDataDumpRow?.skuCode;

  const showCountsSinceSkuStart = !showCountsSinceShiftStartOnly;
  return Math.max(
    // earliest we can query is the shift start
    shiftStart || 0,
    // latest we can query is sku start
    // flag to ignore the sku start
    showCountsSinceSkuStart &&
      // if we're looking at a past shift, must ignore the latest sku
      isCurrentShiftSelected &&
      (skuName || Boolean(skuCode))
      ? skuStartMs || 0
      : 0
  );
}

export function useHubletViewProcess() {
  const isStorybook = useIsStorybook();
  const summaryGroup = useSummaryGroup();
  const visibleGroups = useVisibleGroups();
  const currentGroup = visibleGroups.find(
    group => group.summaryGroup === summaryGroup
  );
  const detailKey = useDetailKey();
  const midgardIdHubletPage = useMidgardIdHubletPage();
  return useMemo(() => {
    if (midgardIdHubletPage) {
      const allGroupsProcessesFlat = visibleGroups.flatMap(
        group => group.processes
      );
      return allGroupsProcessesFlat.find(
        proc => proc.midgardId === midgardIdHubletPage
      );
    }
    const process = currentGroup?.processes?.sort((a, b) => {
      if (!currentGroup.processesSortOrder) {
        return 0;
      }
      return (
        currentGroup.processesSortOrder.indexOf(a.id) -
        currentGroup.processesSortOrder.indexOf(b.id)
      );
    })?.[isStorybook ? 0 : detailKey];
    return process;
  }, [
    currentGroup,
    detailKey,
    isStorybook,
    midgardIdHubletPage,
    visibleGroups,
  ]);
}
export function useLastSegmentDetails(): { segment: any; pnlCategory: any } {
  const isStorybook = useIsStorybook();
  const summaryGroup = useSummaryGroup();
  const detailKey = useDetailKey();
  const visibleGroups = useVisibleGroups();
  const currentGroup = visibleGroups.find(
    group => group.summaryGroup === summaryGroup
  );
  const process = currentGroup?.processes?.[isStorybook ? 0 : detailKey];
  const processId = process?.id;
  const { preferredMetric } = useSettings();
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const dataDump = useHubletViewDataDump();
  const shouldUseDataDumps = useShouldUseDataDumpForHubletView();
  const rollupMetricFromAskr = useRollupDisplayForProcess(processId);
  const mapDataDumpToRollup = useMapDataDumpToRollup();
  const rollupMetric =
    shouldUseDataDumps && dataDump
      ? mapDataDumpToRollup(dataDump)
      : rollupMetricFromAskr;

  const [currentSegment] = useCurrentAskrSegment();

  return getCallAndLastSegmentDetails({
    dataDumpsObjForFullRange: dataDump,
    rollupMetric,
    preferredMetric,
    lastSegment: currentSegment,
    isMidgardDDAEnabled,
  });
}
export function useStatusTimeHubletView(): {
  showCountdown: any;
  isNegative: any;
  displayedTime1: any;
  displayedUnit1: any;
  displayedTime2: any;
  displayedUnit2: any;
  isLessThanOneMinute: any;
} {
  const isStorybook = useIsStorybook();
  const summaryGroup = useSummaryGroup();
  const detailKey = useDetailKey();
  const visibleGroups = useVisibleGroups();
  const currentGroup = visibleGroups.find(
    group => group.summaryGroup === summaryGroup
  );
  const process = currentGroup?.processes?.[isStorybook ? 0 : detailKey];
  const processId = process?.id;
  const { preferredMetric } = useSettings();
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();

  const dataDump = useHubletViewDataDump();
  const shouldUseDataDumps = useShouldUseDataDumpForHubletView();
  const rollupMetricFromAskr = useRollupDisplayForProcess(processId);
  const mapDataDumpToRollup = useMapDataDumpToRollup();
  const rollupMetric =
    shouldUseDataDumps && dataDump
      ? mapDataDumpToRollup(dataDump)
      : rollupMetricFromAskr;
  const [currentSegment] = useCurrentAskrSegment();
  const { segment, pnlCategory } = getCallAndLastSegmentDetails({
    dataDumpsObjForFullRange: dataDump,
    rollupMetric,
    preferredMetric,
    lastSegment: currentSegment,
    isMidgardDDAEnabled,
  });

  const ge = segment?.range.ge ? segment.range.ge : null;

  return useStatusTime({
    pnlCategory,
    processId: processId || "",
    ge,
  });
}
export function useCallTimeDisplay(showCountdown: boolean) {
  const [lastDataDumpRow] = useLastDataDumpRow();
  const messageTimeMs = lastDataDumpRow?.messageTime
    ? lastDataDumpRow?.messageTime
    : null;
  const callStartMs = lastDataDumpRow?.callStart ?? null;
  const callTime =
    callStartMs && messageTimeMs
      ? truncateTime({
          durationMs: messageTimeMs - callStartMs,
          showCountdown,
        })
      : { displayedTime: "--", displayedUnit: "" };
  const displayedCallTime = callTime.displayedTime;
  const displayedCallUnit = callTime.displayedUnit;
  return { displayedCallTime, displayedCallUnit };
}
/** hide the overflow on this page (only scroll on the side panel, or on mobile)
 * AND remain hidden on the grid page (grid page takes care of that)
 */
export function useHideOverflowHubletView() {
  const isDesktopOrLarger = useIsMinWidth(BREAKPOINT_DESKTOP);
  const { isGridLayoutPage } = useGetPageFromPath();
  const isPrint = useMediaQuery("print");
  const shouldHideOverflow = !isGridLayoutPage && isDesktopOrLarger && !isPrint;
  useHideOverflow(shouldHideOverflow);
}

export function useIsCall() {
  const [lastDataDumpRow] = useLastDataDumpRow();
  const callStartMs = lastDataDumpRow?.callStart ?? null;
  const callTarget = lastDataDumpRow?.callTarget ?? null;

  return Boolean(callTarget && callStartMs);
}
export const isMiniModeAtom = atom(false);
export const useIsMiniMode = () => {
  return useAtom(isMiniModeAtom);
};
export const hasUserClickedMinimodeButtonAtom = atom(false);

export const getPnlTitle = (pnlCategory, t, ckey, processId) => {
  if (
    CUSTOMERS_PROCESSES_WITH_CUSTOM_PNL_CATEGORY_NAMES[ckey]?.[processId]?.[
      pnlCategory
    ]
  ) {
    return CUSTOMERS_PROCESSES_WITH_CUSTOM_PNL_CATEGORY_NAMES[ckey][processId][
      pnlCategory
    ];
  } else if (["plantLoss", "notScheduled"].includes(pnlCategory)) {
    return t(PnlCategoriesEnum.notScheduled).toLowerCase();
  } else if (Object.values(PnlCategoriesEnum).includes(pnlCategory)) {
    return t(pnlCategory).toLowerCase();
  } else {
    return pnlCategory;
  }
};
